import {
  type QueryClient,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";
import { modelProfilesQueryKey } from "@/shared/domain/agentThread/hooks/useModelProfiles";

import type {
  OrgLlmConnectionRecord,
  UpsertOrgLlmConnectionRequest,
} from "../OrgLlmProviderRecord";

import { orgQueryKeys } from "./orgQueryKeys";

/**
 * Refetch the org's model list (which carries its connections) and its chat
 * model profiles after a connection is added, changed, verified or deleted,
 * since the connection's status decides which of its models may run.
 */
export function refreshAfterConnectionChange(
  queryClient: QueryClient,
  orgId: string,
) {
  void queryClient.invalidateQueries({
    queryKey: orgQueryKeys.llmProvider(orgId),
  });
  void queryClient.invalidateQueries({
    queryKey: modelProfilesQueryKey(orgId),
  });
}

/** Add one of the org's own LLM connections (premium orgs). */
export function useCreateOrgLlmConnection() {
  const { orgService } = useDomainServices();
  const queryClient = useQueryClient();

  return useMutation<
    OrgLlmConnectionRecord,
    Error,
    { orgId: string; request: UpsertOrgLlmConnectionRequest }
  >({
    mutationFn: ({ orgId, request }) =>
      orgService.createOrgLlmConnection(orgId, request),
    onSuccess: (_connection, { orgId }) =>
      refreshAfterConnectionChange(queryClient, orgId),
  });
}

/** Replace a connection's label and settings (premium orgs). */
export function useUpdateOrgLlmConnection() {
  const { orgService } = useDomainServices();
  const queryClient = useQueryClient();

  return useMutation<
    OrgLlmConnectionRecord,
    Error,
    {
      orgId: string;
      connectionId: string;
      request: UpsertOrgLlmConnectionRequest;
    }
  >({
    mutationFn: ({ orgId, connectionId, request }) =>
      orgService.updateOrgLlmConnection(orgId, connectionId, request),
    onSuccess: (_connection, { orgId }) =>
      refreshAfterConnectionChange(queryClient, orgId),
  });
}

/** Delete a connection no model on the org's list uses. */
export function useDeleteOrgLlmConnection() {
  const { orgService } = useDomainServices();
  const queryClient = useQueryClient();

  return useMutation<void, Error, { orgId: string; connectionId: string }>({
    mutationFn: ({ orgId, connectionId }) =>
      orgService.deleteOrgLlmConnection(orgId, connectionId),
    onSuccess: (_result, { orgId }) =>
      refreshAfterConnectionChange(queryClient, orgId),
  });
}
